import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IPermission {
  _id: string;
  resource: string;
  action: string;
  label: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IUser {
  _id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'SUPER_ADMIN';
  mobileNo?: string;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  permissions: IPermission[];
}

interface AuthState {
  isAuthenticated: boolean;
  user: IUser | null;
  permissions: IPermission[];
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  permissions: [],
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    loginSuccess: (state, action: PayloadAction<{ user: IUser; token?: string }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.permissions = action.payload.user.permissions || [];
      state.loading = false;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.permissions = [];
      state.loading = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.permissions = [];
      state.loading = false;
      state.error = null;
      // Clear localStorage token
      // localStorage.removeItem('admin_authToken');
    },
    clearError: (state) => {
      state.error = null;
    },
    updatePermissions: (state, action: PayloadAction<IPermission[]>) => {
      state.permissions = action.payload;
      if (state.user) {
        state.user.permissions = action.payload;
      }
    },
  },
});

export const {
  setLoading,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  updatePermissions,
} = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectPermissions = (state: { auth: AuthState }) => state.auth.permissions;
export const selectLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectError = (state: { auth: AuthState }) => state.auth.error;
export const selectIsSuperAdmin = (state: { auth: AuthState }) => 
  state.auth.user?.role === 'SUPER_ADMIN';
export const selectIsAdmin = (state: { auth: AuthState }) => 
  state.auth.user?.role === 'ADMIN';